import { BookingAddOnPaymentDto } from '@hqo/resource-booking-client';

import { AddOnCharge, REPRICING_DISPOSITION, chargeFrom } from 'store/api-client/add-on-payment-types';

export interface AddOnReceiptTotal {
  /** Which of the three things the figure is: paid, owed, or our own arithmetic. */
  labelId: string;
  /** Minor units, like every other amount in this flow. */
  amount: number;
  currency: string | null;
}

export interface AddOnReceipt {
  /**
   * ⛔ AN ESTIMATE, AND ONLY EVER ON AN UNPRICED ORDER — see the note on the rule below. Null is NOT
   * "no tax": it is "no figure we are allowed to state", which is every order that was actually
   * charged.
   *
   * ⚠️ IT CARRIES ITS OWN CURRENCY, THE WORK ORDER'S, and is withheld without one. The booking's
   * currency is NOT a substitute: the vendor bills in whatever it bills in, so labelling the
   * vendor's tax with the room's currency would misstate it whenever the two differ.
   */
  tax: { amountCents: number; currency: string } | null;
  total: AddOnReceiptTotal | null;
  /** Non-null only once a work order says it settled. Carries the card and the date with it. */
  charge: AddOnCharge | null;
  /** Minor units already returned. Shown beside the total, never subtracted from it. */
  refundedCents: number | null;
  /**
   * ⛔ NOTHING HAS PRICED THIS ORDER, so no total can be stated — see the note below on why the
   * lines cannot simply be added up. The surface says so in words rather than leaving a gap where
   * a figure belongs.
   */
  isAwaitingPrice: boolean;
}

/*
 * RES-1316 — the add-ons receipt: what the lines come to, what tax we can state, and what the figure
 * under them actually means.
 *
 * ⛔ TAX AND A TOTAL ARE MUTUALLY EXCLUSIVE, AND THAT IS WORK-ORDERS' RULE, NOT A CHOICE MADE HERE.
 * Its `#getEstimatedTaxCents` returns null the moment `totalCents` is set — `!items.length ||
 * totalCents != null` — and checkout writes `totalCents` and `pending_payment` in the SAME update.
 * So the cut is at PRICING, not at settlement, and every order a tenant can pay or has paid reports
 * no estimate at all. A tax estimate is therefore only ever available on an order still awaiting a
 * price: a quote-required item, or a booking still awaiting approval.
 *
 * A TOTAL ON AN UNPRICED ORDER IS WORK-ORDERS' ARITHMETIC, NOT OURS. Two things make the lines
 * untrustworthy here, and work-orders#880 / RBS#196 answer both:
 *
 *   1. `estimatedTaxCents` is inclusive PLUS exclusive tax combined, so adding it to the lines
 *      double-counts the inclusive part: an inclusive 10% rule on a $100 line gives tax $9.09 and a
 *      true total of $100.00, where `lines + tax` claims $109.09. The split is still not exposed, so
 *      summing is still wrong; `estimatedTotalCents` carries `@hqo/billing-tax`'s own figure.
 *   2. A quote-required row stores a price of ZERO with no pricing type, indistinguishable from a
 *      free one, so any total omits what the vendor will quote. `hasUnpricedItems` names that case.
 *
 * So: state `estimatedTotalCents` when every item is priced, the note alone when one is not, and
 * never sum the lines.
 *
 * ⚠️ THE TOTAL IS NAMED BY WHAT IS TRUE OF IT, not by a verb guessed from nine payment states. Paid
 * is paid; anything else billing has priced is a flat "Total", because `rejected`, `payment_failed`
 * and `pending_payment` all carry a real figure and only one of them is owed.
 */
export const addOnReceipt = (payment: BookingAddOnPaymentDto | null): AddOnReceipt => {
  const refundedCents = payment?.refundedAmountCents ? payment.refundedAmountCents : null;

  /*
   * ⭐ BILLING'S OWN FIGURE OUTRANKS OURS WHEREVER IT EXISTS, and it brings its own currency with it.
   * The vendor prices the job, so a charge and our arithmetic can legitimately differ; showing ours
   * under a settled order would state a charge that was never made.
   */
  const charge = payment ? chargeFrom(payment) : null;

  if (charge) {
    return { tax: null, total: null, charge, refundedCents, isAwaitingPrice: false };
  }

  /*
   * ⚠️ WITHOUT A PAYMENT BLOCK WE KNOW NOTHING, which is not the same as knowing it is unpriced. A
   * 403, an unreachable work-orders, or a link that never got created all land here, and claiming
   * the vendor is still pricing would be inventing a state. The rows stand on their own.
   */
  if (!payment) {
    return { tax: null, total: null, charge: null, refundedCents, isAwaitingPrice: false };
  }

  /*
   * ⛔ BILLING HAS PRICED IT, AND A MISSING CURRENCY SILENCES THE FIGURE WITHOUT UNSAYING THE
   * PRICING. `currency` is nullable on both `work_orders` and the add-on payment DTO, so the pair
   * can legitimately arrive half-present. The amount is then withheld, because it cannot be stated
   * in any currency — but "still being priced" would be a second and FALSE claim layered on top of
   * the missing one, so a numeric `totalCents` ends the question of whether billing priced it and
   * the currency decides only whether the figure can be rendered.
   *
   * ⚠️ NOT a claim about existing data. `work_orders.currency` was added later than `total_cents`
   * and never backfilled, but the first add-on work order was raised ~11 weeks after that column
   * landed, so no row of THIS feature sits in that gap. The guard earns its place on the contract
   * alone, which is reason enough and costs nothing.
   */
  /*
   * A charge taken and handed back, with no new price yet. The disposition alone does NOT mean
   * "re-priced" - it means pricing has not run, equally true of a new order behind an approval gate.
   * `totalCents` separates them: work-orders clears it on an amend only when no money moved
   * (`!wasCharged`), so a total surviving it is the refunded charge.
   */
  if (payment.paymentDisposition === REPRICING_DISPOSITION && payment.totalCents != null) {
    return { tax: null, total: null, charge: null, refundedCents, isAwaitingPrice: true };
  }

  /*
   * `amountDueCents` is what is collectable; `totalCents` is what was charged, and after an amend they
   * differ. `??` would conflate "nothing is collectable" with "the service is too old to say", and RBS
   * answers null for every order it has not priced - a rejected quote keeps its `totalCents`, so it
   * would print as the order's Total. The disposition is the version signal: present means trust the
   * amount due, absent means a service that predates both fields.
   */
  const dueCents = payment.paymentDisposition == null ? payment.totalCents : payment.amountDueCents;

  if (typeof dueCents === 'number') {
    return {
      tax: null,
      total: payment.currency ? { labelId: 'add_on_total', amount: dueCents, currency: payment.currency } : null,
      charge: null,
      refundedCents,
      isAwaitingPrice: false,
    };
  }

  /*
   * ⛔ A NULL PAYMENT STATE MEANS TWO OPPOSITE THINGS, AND NOTHING ON THE BLOCK USED TO SEPARATE
   * THEM. work-orders writes null when it has LOOKED at the order and decided to collect nothing —
   * `automatic-checkout.service.ts` for "no items, or everything is free", and a free manual quote
   * does the same — and it LEAVES null in place when it has not looked yet. Its own doc maps NULL to
   * quote `none` / payment `none` and gives a free request as the example, which is only half of it.
   * Reading the state alone therefore prints a brand-new order as free.
   *
   * ⛔ `isPaymentStatePending` IS THAT DISCRIMINATOR, AND IT IS NOT A HEURISTIC (RBS#183). The
   * service mirrors the gates work-orders itself checks before it will decide: the tenant's form
   * answers still being taken, the work order awaiting its own approval, or the booking still
   * awaiting the landlord's. The first is the NORMAL STARTING STATE for any vendor that asks the
   * tenant a question — which is most of them — so this is the common path and not an edge, and it
   * is open during exactly the window in which the tenant reads the receipt they just produced.
   *
   * ⚠️ IT ONLY EVER SPEAKS ABOUT A NULL STATE. The service answers false whenever `paymentState` is
   * non-null, because a non-null state is determined by definition, so it is read only here where
   * the question is live. True means keep saying a price is coming; false means the order really is
   * free and no total is ever arriving.
   *
   * ⚠️ ORDER MATTERS AGAINST THE BRANCH ABOVE. A free MANUAL quote also clears the payment state but
   * writes `totalCents: 0` with the building's currency, so it is caught above and reads "Total
   * $0.00", which is true. Only the figureless case lands here.
   */
  if (!payment.paymentState && !payment.isPaymentStatePending) {
    return { tax: null, total: null, charge: null, refundedCents, isAwaitingPrice: false };
  }

  /*
   * Unpriced, by work-orders' own definition — `totalCents == null` is the very guard that decides
   * whether it hands back an estimate. Two orders reach here: one carrying a payment state, which
   * proves it entered billing, and one whose state work-orders has not settled yet. Neither has a
   * total to state and both may still get one, so the tax estimate is stated as the one figure we
   * were given and no total is claimed over it.
   */
  const estimatedTax =
    typeof payment.estimatedTaxCents === 'number' && payment.currency
      ? { amountCents: payment.estimatedTaxCents, currency: payment.currency }
      : null;

  /*
   * Stated only when work-orders could compute a whole one. `hasUnpricedItems` is reason 2 above: a
   * line the vendor has still to quote, which any total would omit, so the note carries the order.
   */
  const estimatedTotal =
    !payment.hasUnpricedItems && typeof payment.estimatedTotalCents === 'number' && payment.currency
      ? {
          labelId: 'add_on_total_estimated',
          amount: payment.estimatedTotalCents,
          currency: payment.currency,
        }
      : null;

  return {
    tax: estimatedTax,
    total: estimatedTotal,
    charge: null,
    refundedCents,
    isAwaitingPrice: true,
  };
};
